'use client'

import {
  useRef,
  useState,
  type ElementType,
  type KeyboardEvent,
  type MouseEvent,
  type SyntheticEvent,
} from 'react'

import { useRouter } from 'next/navigation'

import { loadTextSource, revertOverrideEdit, saveTextEdit } from '@actions/content-edit'
import { sourceKey } from '@/lib/content-edit/source-key'
import type { EditSource } from '@/lib/content-edit/sources'
import { showToast } from '@/lib/utils/toast'

import { useContentEdit } from './content-edit-provider'

interface EditableTextProps {
  source: EditSource
  value: string
  as?: ElementType
  className?: string
}

/**
 * Inline editable string.
 *
 * With edit mode off (every visitor, always on production) this renders
 * `<Tag className={className}>{value}</Tag>` and nothing else: no handlers, no
 * extra attributes, no server calls. Edit mode adds contentEditable, the dashed
 * outline, and the data-edit-key hook the E2E suite targets.
 */
export function EditableText({ source, value, as: Tag = 'span', className }: EditableTextProps) {
  const { editing, refreshStatus } = useContentEdit()
  const router = useRouter()
  const [text, setText] = useState(value)
  const [overridden, setOverridden] = useState(false)
  const baseHashRef = useRef<string | null>(null)
  const nodeRef = useRef<HTMLElement | null>(null)

  if (!editing) return <Tag className={className}>{text}</Tag>

  // The hash is read at focus, not at render, so a save always compares against
  // the file as it is on disk right now; a second editor's write turns into a
  // CONFLICT instead of a silent clobber.
  const beginEdit = async () => {
    const res = await loadTextSource(source)
    if (res.success && res.data) {
      baseHashRef.current = res.data.baseHash
      if (source.kind === 'override') setOverridden(res.data.value !== null)
    } else {
      baseHashRef.current = null
      showToast({ title: 'Cannot edit', description: res.error, color: 'danger' })
    }
  }

  const commit = async () => {
    const next = nodeRef.current?.textContent ?? ''
    if (next === text || baseHashRef.current === null) return
    const prev = text
    setText(next)
    const res = await saveTextEdit(source, next, baseHashRef.current)
    if (res.success) {
      await refreshStatus()
      router.refresh()
    } else {
      setText(prev)
      if (nodeRef.current) nodeRef.current.textContent = prev
      showToast({ title: 'Save failed', description: res.error, color: 'danger' })
    }
  }

  const revert = async (e: MouseEvent) => {
    // Same wrapper problem as the field itself: this button can render inside a
    // link or a pressable, and a revert must never navigate.
    e.preventDefault()
    e.stopPropagation()
    const res = await revertOverrideEdit(source)
    if (res.success) {
      setOverridden(false)
      await refreshStatus()
      router.refresh()
    } else {
      showToast({ title: 'Revert failed', description: res.error, color: 'danger' })
    }
  }

  // An editable field often sits inside a <Link> or a HeroUI Button (the hero
  // CTAs are both). Pointer input on the field would otherwise activate the
  // wrapper: the anchor navigates away and react-aria's usePress fires the
  // button's onPress from its own onClick handler, so the editor loses the
  // caret before typing a character. Swallowing the click here (preventDefault
  // cancels the anchor's activation behaviour, stopPropagation keeps the press
  // handler from running) fixes every call site at once. Pointer/mouse down is
  // stopped too because usePress starts a press there and applies
  // `user-select: none` to the wrapper, which blocks drag-selecting the text
  // being edited. stopPropagation never cancels a default action, so focus and
  // caret placement still work normally.
  const swallowPointer = (e: SyntheticEvent) => {
    e.stopPropagation()
  }

  const swallowClick = (e: MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      ;(e.target as HTMLElement).blur()
    }
    if (e.key === 'Escape') {
      if (nodeRef.current) nodeRef.current.textContent = text
      ;(e.target as HTMLElement).blur()
    }
  }

  return (
    <>
      <Tag
        ref={nodeRef}
        contentEditable
        suppressContentEditableWarning
        data-edit-key={sourceKey(source)}
        className={`${className ?? ''} rounded-sm outline-dashed outline-1 outline-accent/60 focus:outline-2 focus:outline-accent`}
        onFocus={beginEdit}
        onBlur={commit}
        onKeyDown={onKeyDown}
        onPointerDown={swallowPointer}
        onMouseDown={swallowPointer}
        onClick={swallowClick}
      >
        {text}
      </Tag>
      {source.kind === 'override' && overridden ? (
        <button
          type='button'
          onClick={revert}
          className='ml-1 align-middle text-xs text-warning underline underline-offset-2'
          aria-label='Revert to database value'
        >
          revert
        </button>
      ) : null}
    </>
  )
}
